import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import cn from 'classnames';

import { useDispatch, useSelector } from 'react-redux';
import { selectUserToken } from '../../../redux/features/user/userSelectors';
import { useGetRealtiesInfiniteQuery } from '../../../redux/services/realties/realtiesApi';
import { setTotalSearchResults } from '../../../redux/features/dashboard/dashboardSlice';
import { toast } from 'sonner';

import { ErrorMsg } from '../ErrorMsg';
import { CardRealty } from '../realtyCard/CardRealty';
import { ToggleGroupTemplate } from '../../form/inputs/ToggleGroupTemplate';

import { errorMessages, realtyListToggleData } from '../../../data/constants';
import { LoaderProgress } from '../LoaderProgress';
import { Loader } from '../Loader';
import { ButtonTemplate } from '../buttons/ButtonTemplate';
import { RefreshCcw, ArrowUp, ArrowDown, Tag } from 'lucide-react';

export const RealtyList = ({
  setIsLoading = () => {},
  isSearch = false,
  isPwa = false,
}) => {
  const [error, setError] = useState(null);
  const [activeSeachType, setActiveSeachType] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // desc = newest first
  const [hideSold, setHideSold] = useState(true); // hide sold realties by default

  const lastVerticalScroll = useRef(0);

  const dispatch = useDispatch();
  const userToken = useSelector(selectUserToken);
  const {
    data: realtiesPages,
    isLoading: isRealtiesLoading,
    isFetching: isRealtiesFetching,
    error: realtiesError,
    hasNextPage,
    fetchNextPage,
  } = useGetRealtiesInfiniteQuery(
    {
      token: userToken,
      searchType: activeSeachType,
      sortOrder,
      hideSold,
    },
    { refetchOnMountOrArgChange: 60, skip: isSearch }
  );

  const lastPage = realtiesPages?.pages?.at(-1);
  // Pages can overlap when listings shift between fetches
  const accumulatedRealties = useMemo(() => {
    const seen = new Set();
    return (realtiesPages?.pages ?? [])
      .flatMap(({ data = [] }) => data)
      .filter(({ id }) => !seen.has(id) && seen.add(id));
  }, [realtiesPages?.pages]);

  const handleSortToggle = useCallback(() => {
    setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  const handleSoldToggle = useCallback(() => {
    setHideSold(prev => !prev);
  }, []);

  const loadRealties = useCallback(() => {
    if (!isRealtiesLoading && lastPage && !realtiesError) {
      setError(null);
      dispatch(setTotalSearchResults(lastPage?.total ? lastPage.total : 0));
    }

    if (realtiesError) {
      setError(errorMessages.data);
      toast.error(errorMessages.data);
    }
  }, [isRealtiesLoading, lastPage, realtiesError, dispatch]);

  const handleToggleClick = value => {
    setActiveSeachType(value?.toLowerCase());
  };

  const togglers = useMemo(() => {
    return realtyListToggleData.map(data => {
      return { ...data, handleClick: () => handleToggleClick(data.title) };
    });
  }, []);

  const activeToggleValue =
    realtyListToggleData.find(
      ({ title }) => title.toLowerCase() === activeSeachType
    )?.value ?? realtyListToggleData[0].value;

  const handleScroll = useCallback(() => {
    if (isRealtiesFetching) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    const scrolledToEnd = scrollTop + clientHeight >= scrollHeight - 200;
    const isScrollingForward = scrollTop > lastVerticalScroll.current;
    lastVerticalScroll.current = scrollTop;

    if (scrolledToEnd && isScrollingForward && hasNextPage) {
      fetchNextPage();
    }
  }, [isRealtiesFetching, hasNextPage, fetchNextPage]);

  useEffect(() => {
    setIsLoading(isRealtiesLoading);
  }, [isRealtiesLoading, setIsLoading]);

  useEffect(() => {
    loadRealties();
  }, [loadRealties]);

  const handleRefreshClick = useCallback(() => {
    console.log('refreshed');
    //! add parse refresh trigger endpoint
  }, []);

  const handleScrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isSearch) return; // Only for dashboard, not search

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, isSearch]);

  return (
    <>
      {!isSearch && (
        <>
          <div className="flex justify-between mb-3">
            <ToggleGroupTemplate
              toggleType={'single'}
              controlledValue={activeToggleValue}
              toggleList={togglers}
              className={'justify-start gap-0'}
            />

            <div className="flex gap-2 items-center">
              <span className="block text-xs text-blackColor">
                {lastPage?.meta?.total
                  ? `${accumulatedRealties.length} of ${lastPage.meta.total}`
                  : ''}
              </span>
              <ButtonTemplate
                icon={Tag}
                isIconText={true}
                color={hideSold ? '#100C09' : '#ef4444'}
                size={16}
                handleClick={handleSoldToggle}
                tooltipText={hideSold ? 'Sold hidden' : 'Showing sold'}
                isSmall={true}
              />
              <ButtonTemplate
                icon={sortOrder === 'desc' ? ArrowDown : ArrowUp}
                isIconText={true}
                color={'#100C09'}
                size={16}
                handleClick={handleSortToggle}
                tooltipText={
                  sortOrder === 'desc' ? 'Newest first' : 'Oldest first'
                }
                isSmall={true}
              />
              <ButtonTemplate
                icon={RefreshCcw}
                isIconText={true}
                color={'#100C09'}
                size={16}
                handleClick={handleRefreshClick}
                tooltipText="Refresh list"
                isSmall={true}
              />
            </div>
          </div>

          <div
            className={cn('flex flex-col items-center gap-3', {
              'md:flex-row md:flex-wrap md:items-start': !isPwa,
            })}
          >
            {accumulatedRealties.map(realty => (
              <CardRealty
                key={realty?.id}
                realty={realty}
                isPwa={isPwa}
              />
            ))}

            {accumulatedRealties.length > 9 &&
              (isRealtiesFetching || !hasNextPage) && (
                <div
                  className={cn(
                    'flex flex-col items-center justify-center gap-2 w-full p-6 border border-gray-200 rounded-lg bg-gray-50',
                    { 'xs:w-[360px]': !isPwa }
                  )}
                >
                  {isRealtiesFetching ? (
                    <>
                      <Loader
                        width={32}
                        height={32}
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Loading more items...
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-600 font-medium">
                        {lastPage?.meta?.total
                          ? `${accumulatedRealties.length} of ${lastPage.meta.total} items loaded`
                          : `${accumulatedRealties.length} items loaded`}
                      </span>

                      <span className="text-xs text-gray-500">
                        You have reached the end
                      </span>

                      <ButtonTemplate
                        icon={ArrowUp}
                        isIconText={true}
                        text="Back to top"
                        handleClick={handleScrollToTop}
                        classes="mt-2 bg-white border-gray-300 hover:bg-gray-100"
                        color="#374151"
                        size={16}
                      />
                    </>
                  )}
                </div>
              )}
          </div>
        </>
      )}

      {isRealtiesFetching && <LoaderProgress />}

      {error && (
        <div className="flex justify-center w-full h-20">
          <ErrorMsg message={errorMessages.data} />
        </div>
      )}
    </>
  );
};
